import { useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, Menu, X } from "lucide-react";
import siwonLogo from "@/assets/siwon-logo.png";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

const discoveryTools = [
  { title: "Relationship Harmony Assessment Quiz", href: "/discovery/relationship-quiz" },
  { title: "Life Transformation Diagnostic Quiz", href: "/discovery/life-quiz" },
  { title: "Relationship Wisdom Board Game", href: "/games/relationship-wisdom-board.html", external: true },
];

export const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [discoveryOpen, setDiscoveryOpen] = useState(false);

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-background/95 backdrop-blur-md border-b border-border/50">
      <div className="container flex items-center justify-between h-14 md:h-16">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-2 group">
          <img 
            src={siwonLogo} 
            alt="Siwon Galaxy Logo" 
            className="h-12 md:h-14 w-auto transition-transform group-hover:scale-105"
          />
          <span className="text-[10px] md:text-xs text-muted-foreground tracking-widest uppercase">
            Silence to Wonderment
          </span>
        </Link>

        {/* Desktop Navigation - All buttons visible */}
        <nav className="hidden lg:flex items-center gap-0.5">
          <Link 
            to="/" 
            className="px-2 py-1.5 text-xs font-medium rounded-md hover:bg-accent hover:text-accent-foreground transition-colors text-center"
          >
            Home
          </Link>

          <Link 
            to="/about" 
            className="px-2 py-1.5 text-xs font-medium rounded-md hover:bg-accent hover:text-accent-foreground transition-colors text-center"
          >
            About
          </Link>

          {/* Discovery Tools Dropdown */}
          <div className="relative">
            <button
              onClick={() => setDiscoveryOpen(!discoveryOpen)}
              onBlur={() => setTimeout(() => setDiscoveryOpen(false), 150)}
              className="flex items-center gap-0.5 px-2 py-1.5 text-xs font-medium rounded-md hover:bg-accent hover:text-accent-foreground transition-colors text-center whitespace-nowrap"
            >
              Quiz
              <ChevronDown className={cn("h-3 w-3 transition-transform", discoveryOpen && "rotate-180")} />
            </button>
            {discoveryOpen && (
              <div className="absolute top-full left-0 mt-1 w-72 bg-popover border border-border rounded-lg shadow-lg z-50">
                <ul className="py-2">
                  {discoveryTools.map((tool) => (
                    <li key={tool.href}>
                      {tool.external ? (
                        <a
                          href={tool.href}
                          className="block px-4 py-2 text-sm hover:bg-accent hover:text-accent-foreground transition-colors"
                          onClick={() => setDiscoveryOpen(false)}
                          data-testid={`quiz-item-${tool.href}`}
                        >
                          {tool.title}
                        </a>
                      ) : (
                        <Link
                          to={tool.href}
                          className="block px-4 py-2 text-sm hover:bg-accent hover:text-accent-foreground transition-colors"
                          onClick={() => setDiscoveryOpen(false)}
                        >
                          {tool.title}
                        </Link>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          <Link 
            to="/harmony-app" 
            className="px-2 py-1.5 text-xs font-medium rounded-full bg-accent text-primary-foreground hover:bg-accent/90 transition-colors text-center whitespace-nowrap"
          >
            Siwon Harmony App
          </Link>

          <Link 
            to="/membership-programs" 
            className="px-2 py-1.5 text-xs font-medium rounded-md hover:bg-accent hover:text-accent-foreground transition-colors text-center whitespace-nowrap"
          >
            Membership
          </Link>

          <Link 
            to="/free-webinar" 
            className="px-2 py-1.5 text-xs font-medium rounded-full bg-accent text-primary-foreground hover:bg-accent/90 transition-colors text-center whitespace-nowrap"
          >
            Join Free Webinar
          </Link>

          <Link 
            to="/wisdom-board" 
            className="px-2 py-1.5 text-xs font-medium rounded-md hover:bg-accent hover:text-accent-foreground transition-colors text-center whitespace-nowrap"
          >
            Wisdom Board
          </Link>

          <Link 
            to="/the-path" 
            className="px-2 py-1.5 text-xs font-medium rounded-md hover:bg-accent hover:text-accent-foreground transition-colors text-center whitespace-nowrap"
          >
            Spiritual Path
          </Link>

          <Button asChild className="ml-1 btn-lime text-xs px-3 py-1">
            <a href="https://learn.siwongalaxy.com" target="_blank" rel="noopener noreferrer">Login LMS</a>
          </Button>
        </nav>

        {/* Mobile Menu Button */}
        <button
          className="lg:hidden p-2 text-foreground"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          aria-label="Toggle menu"
        >
          {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* Mobile Navigation */}
      {mobileMenuOpen && (
        <div className="lg:hidden bg-background border-t border-border/50">
          <nav className="container py-4 flex flex-col gap-2">
            <Link 
              to="/" 
              className="px-4 py-2 text-sm font-medium hover:bg-accent rounded-md transition-colors"
              onClick={() => setMobileMenuOpen(false)}
            >
              Home
            </Link>
            <Link 
              to="/about" 
              className="px-4 py-2 text-sm font-medium hover:bg-accent rounded-md transition-colors"
              onClick={() => setMobileMenuOpen(false)}
            >
              About Us
            </Link>
            
            {/* Discovery Tools Accordion */}
            <div>
              <button
                className="w-full px-4 py-2 text-sm font-medium hover:bg-accent rounded-md transition-colors flex items-center justify-between"
                onClick={() => setDiscoveryOpen(!discoveryOpen)}
              >
                Diagnostic Quiz & Assessment Tools
                <ChevronDown className={cn("h-4 w-4 transition-transform", discoveryOpen && "rotate-180")} />
              </button>
              {discoveryOpen && (
                <div className="pl-4 mt-1 space-y-1">
                  {discoveryTools.map((tool) => (
                    tool.external ? (
                      <a
                        key={tool.href}
                        href={tool.href}
                        className="block px-4 py-2 text-sm text-muted-foreground hover:text-foreground hover:bg-accent rounded-md transition-colors"
                        onClick={() => setMobileMenuOpen(false)}
                        data-testid={`quiz-item-mobile-${tool.href}`}
                      >
                        {tool.title}
                      </a>
                    ) : (
                      <Link
                        key={tool.href}
                        to={tool.href}
                        className="block px-4 py-2 text-sm text-muted-foreground hover:text-foreground hover:bg-accent rounded-md transition-colors"
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        {tool.title}
                      </Link>
                    )
                  ))}
                </div>
              )}
            </div>

            <Link 
              to="/harmony-app" 
              className="px-4 py-2 text-sm font-medium bg-accent text-primary-foreground rounded-md transition-colors"
              onClick={() => setMobileMenuOpen(false)}
            >
              Siwon Harmony App
            </Link>
            <Link 
              to="/membership-programs" 
              className="px-4 py-2 text-sm font-medium hover:bg-accent rounded-md transition-colors"
              onClick={() => setMobileMenuOpen(false)}
            >
              Membership
            </Link>
            <Link 
              to="/free-webinar" 
              className="px-4 py-2 text-sm font-medium bg-accent text-primary-foreground rounded-md transition-colors"
              onClick={() => setMobileMenuOpen(false)}
            >
              Join Free Webinar
            </Link>
            <Link 
              to="/wisdom-board" 
              className="px-4 py-2 text-sm font-medium hover:bg-accent rounded-md transition-colors"
              onClick={() => setMobileMenuOpen(false)}
            >
              Wisdom Board
            </Link>
            <Link 
              to="/the-path" 
              className="px-4 py-2 text-sm font-medium hover:bg-accent rounded-md transition-colors"
              onClick={() => setMobileMenuOpen(false)}
            >
              Spiritual Path
            </Link>
            
            <div className="pt-2 border-t border-border/50 mt-2">
              <a 
                href="https://learn.siwongalaxy.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="block px-4 py-2 text-sm font-medium btn-lime text-center rounded-md"
                onClick={() => setMobileMenuOpen(false)}
              >
                Login LMS
              </a>
            </div>
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;
